import { useCallback, useMemo } from 'react';

import type { SliderProps } from '@arco-design/web-react';
import { Slider } from '@arco-design/web-react';

import { DEFAULT_SLIDER_MARK_COUNT, buildSliderMarks } from './sliderMarks';

export interface CapacitySliderProps extends Omit<SliderProps, 'marks' | 'min' | 'max'> {
  min: number;
  max: number;
  /** 刻度展示单位，默认 Gi */
  unit?: string;
  /** 刻度值大于 1024 且单位为 Gi 时是否转换为 Ti，默认 true */
  translateUnit?: boolean;
  /** 滑条刻度数量（含首尾），默认 5 */
  markCount?: number;
  /** 只读：输入框不可编辑，滑条仍可拖动（除非 disabled） */
  readOnly?: boolean;
}

function normalizeSliderValue(value: unknown, min: number, max: number): number {
  if (value == null || value === '') return min;
  const raw = Array.isArray(value) ? value[0] : value;
  const parsed = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

/** 容量滑条：按 min/max 自动生成刻度，默认单位 Gi，宽度随父容器自适应 */
export function CapacitySlider({
  min,
  max,
  unit = 'Gi',
  translateUnit = true,
  markCount = DEFAULT_SLIDER_MARK_COUNT,
  readOnly = false,
  disabled,
  showInput = {
    hideControl: false,
    style: { width: 80, marginLeft: 10 },
  },
  value,
  onChange,
  ...rest
}: CapacitySliderProps) {
  const marks = useMemo(() => buildSliderMarks(min, max, translateUnit, markCount, unit), [markCount, max, min, translateUnit, unit]);
  const sliderValue = useMemo(() => normalizeSliderValue(value, min, max), [max, min, value]);

  const handleChange = useCallback(
    (next: number | number[]) => {
      onChange?.(Array.isArray(next) ? next[0] : next);
    },
    [onChange],
  );

  const mergedShowInput =
    showInput === false
      ? false
      : {
          hideControl: false,
          readOnly,
          style: { width: 80, marginLeft: 10 },
          ...(typeof showInput === 'object' ? showInput : {}),
        };

  return (
    <Slider
      disabled={disabled}
      showInput={mergedShowInput}
      marks={marks}
      max={max}
      min={min}
      value={sliderValue}
      onChange={handleChange}
      {...rest}
    />
  );
}
